import React from 'react';
import { useAppContext } from '@/context/app';
import Auth from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { IUser } from '@/types/user';

type UseApiHook = {
    token?: string;
    user?: IUser | null
    apiBase: string;
    buildUri: <Q = Record<string, string>>(path: string, query?: Q) => string;
    apiPost: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiPut: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiPatch: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    /**
     * POST a multipart/form-data body. Content-Type is intentionally not set so
     * the browser adds the multipart boundary. Same auth/refresh logic as apiPost.
     */
    apiPostFormData: <R = unknown>(
        path: string,
        body: FormData,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiGet: <R = unknown, Q = Record<string, string | string[]>>(
        path: string,
        query?: Q,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiDelete: <R = unknown>(
        path: string,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
};

export interface IApiError {
    status: number;
    error: unknown;
}

const base = `${import.meta.env.VITE_APP_REST_API_DOMAIN}`;

// Shared across all useApi() instances so concurrent 401s trigger a single
// Cognito refresh instead of one per in-flight request.
let refreshPromise: Promise<string> | null = null;

const useApi = (apiBase: string = base): UseApiHook => {
    const navigate = useNavigate();
    const { auth, setAuthUserInfo } = useAppContext();
    const { token, user } = auth;

    const buildUri = <Q = Record<string, string>>(
        path: string,
        query?: Q,
    ): string => {
        const fullUrl = `${apiBase}${path}`;

        if (!query || Object.keys(query).length === 0) {
            return fullUrl;
        }

        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value != null) {
                params.append(key, String(value));
            }
        });

        return `${fullUrl}?${params.toString()}`;
    };

    const handleResponse = async <R>(response: Response): Promise<R> => {
        if (response.ok) {
            if (response.status === 204) return undefined as R;

            // Tolerate empty bodies (e.g. 202 Accepted); otherwise a JSON parse
            // failure would be mistaken for an auth error and the request retried.
            const text = await response.text();

            return (text ? JSON.parse(text) : undefined) as R;
        }

        const errorData = await response
            .json()
            .catch(() => ({ message: 'Unknown error' }));
        const error: IApiError = {
            status: response.status,
            error: errorData,
        };
        throw new Error(JSON.stringify(error));
    };

    const createRequestConfig = (
        method: string,
        body?: unknown,
        commonHeaders: Record<string, string> = {},
        signal?: AbortSignal | null | undefined,
    ): RequestInit => {
        const isFormData =
            typeof FormData !== 'undefined' && body instanceof FormData;
        const config: RequestInit = {
            method,
            headers: {
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                ...(token && { Authorization: `Bearer ${token}` }),
                ...commonHeaders,
            },
            signal,
        };

        if (isFormData) {
            config.body = body;
        } else if (body !== undefined) {
            config.body = JSON.stringify(body);
        }

        return config;
    };

    // Auth.currentSession() already refreshes the Cognito session under the hood
    // when the cached ID token is expired but the refresh token is still valid,
    // so simply re-reading it is enough to obtain (and persist) a fresh token.
    const getFreshToken = async (): Promise<string> => {
        if (!refreshPromise) {
            refreshPromise = (async () => {
                const session = await Auth.currentSession();
                const idToken = session.getIdToken();
                const newToken = idToken.getJwtToken();

                setAuthUserInfo({
                    ...auth,
                    token: newToken,
                    email: idToken.decodePayload()['email'] as string,
                    expireAt: idToken.getExpiration(),
                });

                return newToken;
            })().finally(() => {
                refreshPromise = null;
            });
        }

        return refreshPromise;
    };

    const getErrorStatus = (error: unknown): number | undefined => {
        if (!(error instanceof Error)) return undefined;
        try {
            return (JSON.parse(error.message) as IApiError).status;
        } catch {
            return undefined;
        }
    };

    const fetchWithFailHandler = async <R>(
        url: string,
        config: RequestInit,
    ): Promise<R> => {
        try {
            const response = await fetch(url, config);

            return await handleResponse<R>(response);
        } catch (error) {
            if (config.signal?.aborted) {
                throw error;
            }

            const status = getErrorStatus(error);

            // status === undefined means the browser never delivered a parseable response body -
            // e.g. it reports "blocked by CORS policy" / "Failed to fetch" when API Gateway
            // rejects an expired Cognito token before the Lambda (whose `cors` config never
            // gets a chance to run) adds any CORS header. Treat that the same as a real 401:
            // refresh the token and retry once, instead of surfacing a false CORS error.
            if (status !== undefined && status !== 401) {
                throw error;
            }

            let newToken: string;
            try {
                newToken = await getFreshToken();
            } catch (refreshErr) {
                console.error('Session expired, log in again.', refreshErr);
                await Auth.signOut({ global: true }).catch(() => undefined);
                // apiPut<{ state: string }, void>('/users/me', {
                //     state: 'disconnected',
                // }).catch(() => undefined);
                navigate('/auth/login');

                throw new Error('Session expired, please log in again.');
            }

            const retryConfig: RequestInit = {
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: `Bearer ${newToken}`,
                },
            };

            return handleResponse<R>(await fetch(url, retryConfig));
        }
    };

    const apiPost = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('POST', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiPostFormData = React.useCallback(
        async <R>(
            path: string,
            body: FormData,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('POST', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiPut = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('PUT', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiPatch = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('PATCH', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiGet = React.useCallback(
        async <R, Q = Record<string, string | string[]>>(
            path: string,
            query?: Q,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {

            return fetchWithFailHandler<R>(
                buildUri(path, query),
                createRequestConfig('GET', undefined, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiDelete = React.useCallback(
        async <R>(
            path: string,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('DELETE', undefined, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    return {
        token,
        user,
        apiBase,
        buildUri,
        apiPost,
        apiPostFormData,
        apiPut,
        apiPatch,
        apiGet,
        apiDelete,
    };
};

export default useApi;
